/**
 * Shared validation utilities for parking button text determination
 *
 * These utilities are used across multiple parking components to ensure
 * consistent validation logic for payment methods, vehicles, and time selection.
 */

/**
 * Validates if the selected end time is valid for parking
 * End time must be in the future (greater than current time)
 *
 * @param endDate - The selected end date/time
 * @returns true if end time is valid (in the future)
 */
export const hasValidEndTime = (endDate: Date | undefined | null): boolean => {
  return !!endDate && endDate > new Date();
};

/**
 * Validates if parking duration meets minimum requirements
 * Parking must be at least 2 minutes in duration
 *
 * @param minuteDif - Minutes between now and end time
 * @returns true if duration is valid (>= 2 minutes)
 */
export const hasValidParkingDuration = (
  minuteDif: number | undefined
): boolean => {
  const MIN_PARKING_MINUTES = 2;
  return minuteDif !== undefined && minuteDif >= MIN_PARKING_MINUTES;
};

/**
 * Checks if payment method is missing and returns appropriate translation key
 *
 * Priority:
 * 1. If hasOtherPaymentMethods -> user needs to select from existing cards
 * 2. If noPaymentMethodAndCompanyWontPay -> user needs to add a payment method
 *
 * @param hasOtherPaymentMethods - User has payment methods but none selected
 * @param noPaymentMethodAndCompanyWontPay - No payment method and company won't pay
 * @returns Translation key or null if payment is valid
 */
export const getMissingPaymentTextKey = (
  hasOtherPaymentMethods: boolean,
  noPaymentMethodAndCompanyWontPay: boolean
): string | null => {
  if (hasOtherPaymentMethods) {
    return 'parking.parkingProcess.noCardSelected';
  }
  if (noPaymentMethodAndCompanyWontPay) {
    return 'onboarding.addPaymentMethod';
  }
  return null;
};

/**
 * Checks if vehicle is missing and returns appropriate translation key
 *
 * Priority:
 * 1. If no vehicle AND no vehicles available -> user needs to add a vehicle
 * 2. If no vehicle BUT vehicles available -> user needs to select a vehicle
 *
 * @param selectedVehicle - Currently selected vehicle (can be undefined)
 * @param hasVehicles - Whether user has any vehicles in their account
 * @returns Translation key or null if vehicle is valid
 */
export const getMissingVehicleTextKey = (
  selectedVehicle: any,
  hasVehicles: boolean
): string | null => {
  if (!selectedVehicle && !hasVehicles) {
    return 'onboarding.addVehicle';
  }
  if (!selectedVehicle && hasVehicles) {
    return 'parking.parkingProcess.noVehicleSelected';
  }
  return null;
};
